<script setup>
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
  
</script>

<template>
  <div class="wrap row">
    <jumbo title="Fingerprints & Hashes">

    Fingerprints and hashes play essential roles in identifying and verifying the integrity of web content. A website fingerprint is a unique identifier generated from various characteristics and attributes of a website. These characteristics include:<br><br>

<ul>
  <li><strong>HTML structure</strong>: The layout and structure of the HTML code comprising the website's pages.</li>
  <li><strong>JavaScript code</strong>: The presence, structure, and behavior of JavaScript code embedded within the website.</li>
  <li><strong>CSS styles</strong>: The styling and formatting applied to the website's elements using Cascading Style Sheets (CSS).</li>
  <li><strong>Response headers</strong>: Metadata included in the HTTP response headers sent by the server.</li>

</ul>

By analyzing these elements, a website fingerprint can be generated, serving as a unique signature that distinguishes one website from another, or one actor from another. As part of our effort to connect the web we take a multitude of different fingerprints and hashes, using the following endpoints you retrieve additional information on any given hash or fingerprint.

    </jumbo>


    <doc-section title="Get number of observations">
      <template v-slot:left>
        The number of observations for any given hash can be found by sending a POST request, with a json body to /relations/hash/count. The json should naturally be valid, and have a key named “inputs” which should be an array of no more than 100 strings. These strings are matched against all known artifacts. <br><br>
The response is an object with all hashes with results as keys, with the corresponding value being the number of ips and domains respectively. It should be noted that if one of the input strings is not present in the output, it means the we have not results for that fingerprint/hash
      </template>

      <template v-slot:right>
        <code-block title="/relations/hash/count" :json="true" method="POST">
{"input":["9bf8a0455b048a4b12427db176ba067f8dafddc04e18c8897194ff813121b646","96951336fb841fb756543ac5ddcbf7e7551ecb0c04c53923ab0233a6ebbe353c","9faaa9e51cea5853e4f1bc2060e9581a81312edab1377dc49e064ba8e97db96c","c7ee1e61b3cdf67445e4018719691270f826c73e7ee5c6ba588493610ca90bed","d2127bf3e3bacefb34d4a62e0aecbbdb426203cc9f3473c6062ba50b82eb3704"]}
        </code-block>

        <json-block title="Response">
{"data":{"96951336fb841fb756543ac5ddcbf7e7551ecb0c04c53923ab0233a6ebbe353c":{"domains":2,"ips":5},"9bf8a0455b048a4b12427db176ba067f8dafddc04e18c8897194ff813121b646":{"domains":2,"ips":4},"9faaa9e51cea5853e4f1bc2060e9581a81312edab1377dc49e064ba8e97db96c":{"domains":835,"ips":661},"c7ee1e61b3cdf67445e4018719691270f826c73e7ee5c6ba588493610ca90bed":{"domains":2,"ips":5},"d2127bf3e3bacefb34d4a62e0aecbbdb426203cc9f3473c6062ba50b82eb3704":{"domains":5,"ips":10}},"status":"success"}
        </json-block>
      </template>
    </doc-section>


    <doc-section title="Get observations">
      <template v-slot:left>
Using a single fingerprint/hash it is possible to view all domains and IP’s matching it. This is done by sending a POST request, with a json body, to /relations/hash. The json should have a key named “input” which should be a string, the string has a maximum length of 400 characters. 
<br><br>
The response contains two lists, one of domains and the other of IPs.
      </template>

      <template v-slot:right>
        <code-block title="/relations/hash" :json="true" method="POST">
{"input":"6669780479e747e8fe7202ad73f1c1f19871285b12cbcd789133a855cd27fa3c"}
        </code-block>

        <json-block title="Response">
{"data":{"Hash":"6669780479e747e8fe7202ad73f1c1f19871285b12cbcd789133a855cd27fa3c","Domains":["meta-restoreweb.pages.dev","rpc-metafix.pages.dev"],"IPs":["172.66.44.169","172.66.44.236","172.66.47.20","188.114.96.3","188.114.96.12"],"CountDomains":2,"CountIPs":5},"status":"success"}
        </json-block>
      
      </template>
    </doc-section>

  </div>
</template>

<style scoped>
.wrap {
  height: 100%;
  overflow-y: scroll;
}

.wrap > * {
  margin-top: 2.5rem;
}
.wrap > *:not(:first-child) {
  border-top: 2px solid #1e252f;
  padding-top: 2.5rem;
}
</style>
